import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

export const ThemeContext = createContext();

const STORAGE_KEY = 'theme';

/**
 * Light / dark, with the seller's choice remembered.
 *
 * Three states rather than two: 'light', 'dark', and 'system'. A seller who
 * never touches the toggle follows their operating system and their app
 * changes with it at sunset — which is what most people expect and what a
 * plain boolean cannot express. Choosing explicitly pins it.
 *
 * The class is written to <html> before first paint by a snippet in
 * index.html, so there is no white flash on load for a dark-mode user. This
 * provider keeps it in sync afterwards.
 */
// What someone who has never chosen gets. Dark, deliberately — the house
// style — rather than 'system'. Matched exactly by the pre-paint snippet in
// public/index.html; if the two ever disagree the page flashes the wrong
// colour before React mounts.
const DEFAULT_PREFERENCE = 'dark';

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_PREFERENCE; } catch { return DEFAULT_PREFERENCE; }
  });

  const systemDark = () => window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
  const [isDark, setIsDark] = useState(() => {
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch { /* private mode */ }
    if (!saved) return DEFAULT_PREFERENCE === 'dark';
    return saved === 'system' ? systemDark() : saved === 'dark';
  });

  useEffect(() => {
    // 'system' still works — it is what a seller gets if they pick it from
    // Settings — it simply is not what an untouched browser starts on.
    const resolve = () => (preference === 'system' ? systemDark() : preference === 'dark');
    const apply = () => {
      const dark = resolve();
      setIsDark(dark);
      document.documentElement.classList.toggle('dark', dark);
      // Lets native controls (scrollbars, date pickers) match the theme.
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    };
    apply();

    if (preference !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, [preference]);

  const setTheme = useCallback((next) => {
    setPreference(next);
    try {
      // 'system' is STORED rather than cleared. Removing the key would put the
      // account back to the dark default on the next load, silently undoing the
      // choice they just made.
      localStorage.setItem(STORAGE_KEY, next);
    } catch { /* private mode — the choice just does not persist */ }
  }, []);

  // The toggle flips to the OPPOSITE of what is on screen, whatever the
  // preference was. Cycling light → dark → system through one button is a
  // guessing game for the person pressing it.
  const toggle = useCallback(() => setTheme(isDark ? 'light' : 'dark'), [isDark, setTheme]);

  const value = useMemo(() => ({ preference, isDark, setTheme, toggle }), [preference, isDark, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
